/**
 * The books' Sales and Finance feed (docs/plans/scores.md S4) — what BattleBooks publishes for department goals:
 * `sales/now` (opsSales: leads in, estimates sent, won / lost, win rate, open pipeline by stage, days lead → quote →
 * won) and `finance/now` (opsFinance: invoices sent / paid with days to paid, money collected, receivables aging, bank
 * to reconcile, receipts not posted, filings with on-time flags, money budgets) — battlebooks functions/src/
 * salesFinance.ts + lib/reports/booksFeed.ts. Each is dated rows (one per lead, bid, invoice, payment …) since the 1st
 * of the month four months back, so any day / week / month goal counts here, on the device, over its own window.
 *
 * This file: normalise the two docs (booksFeed), the goal metrics they count (BOOKS_GOAL_METRICS, merged into
 * deptgoals' menu), Sales' and Finance's presets, and the counts (BOOKS_COUNTS — deptgoals hands sales:* and
 * finance:* keys here). Every count returns { value, rows, basis, notes } like fieldgoals: `basis` = what was counted,
 * `notes` = what was left out. No feed on the device yet → { value: null, feed } ("waiting"). Pure — no I/O.
 */
import { localDateStr } from './time.js';
import { addDaysStr } from './phonestats.js';

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const arr = (v) => (Array.isArray(v) ? v.filter((r) => r && typeof r === 'object') : []);
const isDay = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
const inWin = (date, w) => !!date && date >= w.from && date <= w.to;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const money = (v) => `${v < 0 ? '−' : ''}$${Math.abs(Math.round(Number(v) || 0)).toLocaleString('en-CA')}`;
const money2 = (v) => `${v < 0 ? '−' : ''}$${Math.abs(Number(v) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STALE_DAYS = 7;

const dated = (rows) => arr(rows).filter((r) => isDay(r.date));

/** opsSales/now → the Sales feed, or null when it hasn't reached the device (members without a Sales grant never get it). */
export function normSalesFeed(doc) {
  if (!doc || typeof doc !== 'object' || !isDay(doc.today)) return null;
  return {
    ready: true, today: doc.today, from: isDay(doc.from) ? doc.from : doc.today, publishedAt: num(doc.publishedAt),
    leads: dated(doc.leads), sent: dated(doc.sent), won: dated(doc.won), lost: dated(doc.lost), days: dated(doc.days),
    totals: doc.totals || {}, pipeline: { stages: arr(doc.pipeline?.stages), count: num(doc.pipeline?.count) || 0, value: num(doc.pipeline?.value) || 0 },
    avgDays: { leadToQuote: num(doc.avgDays?.leadToQuote), quoteToWon: num(doc.avgDays?.quoteToWon) },
    leftOut: { undatedLeads: num(doc.leftOut?.undatedLeads) || 0, changeOrders: num(doc.leftOut?.changeOrders) || 0 },
  };
}

/** opsFinance/now → the Finance feed, or null. */
export function normFinanceFeed(doc) {
  if (!doc || typeof doc !== 'object' || !isDay(doc.today)) return null;
  const queue = (q, key) => ({ count: num(q?.[key]) || 0, over7: num(q?.over7) || 0, oldest: isDay(q?.oldest) ? q.oldest : null, rows: dated(q?.rows) });
  return {
    ready: true, today: doc.today, from: isDay(doc.from) ? doc.from : doc.today, publishedAt: num(doc.publishedAt),
    sent: dated(doc.sent), paid: dated(doc.paid), collected: dated(doc.collected), days: dated(doc.days), totals: doc.totals || {},
    ar: { total: num(doc.ar?.total) || 0, overdue: num(doc.ar?.overdue) || 0, overdueCount: num(doc.ar?.overdueCount) || 0, aging: doc.ar?.aging || null },
    bank: queue(doc.bank, 'unreconciled'), receipts: queue(doc.receipts, 'unposted'),
    filings: arr(doc.filings).filter((r) => isDay(r.due)),
    budgets: { rows: arr(doc.budgets?.rows) },
    leftOut: { paidNoPayment: num(doc.leftOut?.paidNoPayment) || 0, draftInvoices: num(doc.leftOut?.draftInvoices) || 0 },
  };
}

/** Both feeds from the two docs. → { sales, finance } (each null until it's on the device) */
export const booksFeed = ({ sales = null, finance = null } = {}) => ({ sales: normSalesFeed(sales), finance: normFinanceFeed(finance) });

// ── the goal menu ────────────────────────────────────────────────────────────────────────────────────────────────

export const BOOKS_GOAL_METRICS = Object.freeze({
  'sales:leads': { label: 'Leads in', unit: '', kind: 'count', direction: 'up', hint: 'new leads, from the books: BattleBids lead records (intake forms, the website, Add a lead) and lead-stage jobs — one per person, on the day they came in', feed: 'sales' },
  'sales:estimatesSent': { label: 'Estimates sent', unit: '', kind: 'count', direction: 'up', hint: 'bids sent from BattleBids, on the day they went out (change orders not counted)', feed: 'sales' },
  'sales:won': { label: 'Contracts won', unit: '', kind: 'count', direction: 'up', hint: 'bids accepted / converted, on the day they were signed', feed: 'sales' },
  'sales:wonValue': { label: 'Won ($)', unit: '$', kind: 'count', direction: 'up', hint: 'the pre-tax value of bids signed in the period', feed: 'sales' },
  'sales:winRate': { label: 'Win rate', unit: '%', kind: 'rate', direction: 'up', hint: 'bids won ÷ bids decided (won + declined / expired) in the period', feed: 'sales' },
  'finance:collected': { label: 'Money collected', unit: '$', kind: 'count', direction: 'up', hint: 'customer payments recorded in the books in the period (refunds taken off)', feed: 'finance' },
  'finance:invoicesSent': { label: 'Invoices sent', unit: '', kind: 'count', direction: 'up', hint: 'invoices sent in the period (drafts not counted)', feed: 'finance' },
  'finance:paidWithin14Pct': { label: 'Paid within 14 d', unit: '%', kind: 'rate', direction: 'up', hint: 'invoices paid in full in the period that were paid within 14 days of being sent', feed: 'finance' },
  'finance:bankRecStale': { label: 'Bank rec current', unit: '', kind: 'level', direction: 'down', hint: 'bank lines not reconciled that are more than 7 days old, as they stand — a limit', feed: 'finance' },
  'finance:receiptsStale': { label: 'Receipts posted', unit: '', kind: 'level', direction: 'down', hint: 'receipts in the books not posted yet (draft / review) that are more than 7 days old, as they stand — a limit', feed: 'finance' },
  'finance:filingsOnTimePct': { label: 'Filings on time', unit: '%', kind: 'rate', direction: 'up', hint: 'filings due in the period (GST, payroll, returns, instalments) done by their due date — none due reads 100%', feed: 'finance' },
  'finance:budgetsOnTrackPct': { label: 'Budgets on track', unit: '%', kind: 'level', direction: 'up', hint: 'money budgets this period not over their limit and not heading over it, as they stand', feed: 'finance' },
});

/** Sales' presets (S4): leads in a week · 5, estimates sent a week · 3, won a month · $ (from your history), win rate a month · 40%. */
export const SALES_PRESETS = Object.freeze([
  { key: 'sales:leads', period: 'week', target: 5 },
  { key: 'sales:estimatesSent', period: 'week', target: 3 },
  { key: 'sales:wonValue', period: 'month', target: null },
  { key: 'sales:winRate', period: 'month', target: 40 },
]);

/** Finance's presets (S4): money collected a month · $ (from your history), paid within 14 d · 80%, bank rec and receipts · 0 older than 7 d, filings on time · 100%. */
export const FINANCE_PRESETS = Object.freeze([
  { key: 'finance:collected', period: 'month', target: null },
  { key: 'finance:paidWithin14Pct', period: 'month', target: 80 },
  { key: 'finance:bankRecStale', period: 'week', target: 0, direction: 'down' },
  { key: 'finance:receiptsStale', period: 'week', target: 0, direction: 'down' },
  { key: 'finance:filingsOnTimePct', period: 'month', target: 100 },
]);

// ── the counts ───────────────────────────────────────────────────────────────────────────────────────────────────

/** The feed for a key, or the "waiting" answer; a window starting before the feed's first day can't be counted. */
function feedFor(which, w, data) {
  const f = data[which];
  if (!f?.ready) return { miss: { value: null, feed: which } };
  if (w.from < f.from) return { miss: { value: null, notes: [`the books' feed starts ${f.from} — earlier periods aren't counted`] } };
  return { f };
}

const leadTitle = (r) => `${r.name || 'Lead'}${r.source ? ` · ${r.source}` : ''}${r.via === 'project' ? ' · job' : ''}`;
const bidTitle = (r) => [r.number, r.customer, r.value ? money(r.value) : null].filter(Boolean).join(' · ');

export function salesLeads(dept, w, data = {}) {
  const { f, miss } = feedFor('sales', w, data); if (miss) return miss;
  const rows = f.leads.filter((r) => inWin(r.date, w));
  const jobs = rows.filter((r) => r.via === 'project').length;
  const notes = [];
  if (f.leftOut.undatedLeads) notes.push(`${plural(f.leftOut.undatedLeads, 'lead record')} with no date left out`);
  notes.push('a person with a lead record and a lead job counts once; jobs booked with no lead stage aren’t leads');
  return { value: rows.length, rows: rows.map((r) => ({ title: leadTitle(r), date: r.date, customerId: r.customerId || null })), basis: `${plural(rows.length, 'lead')}: ${rows.length - jobs} BattleBids lead record${rows.length - jobs === 1 ? '' : 's'}, ${plural(jobs, 'lead job')} (intake form / website)`, notes };
}

export function salesSent(dept, w, data = {}) {
  const { f, miss } = feedFor('sales', w, data); if (miss) return miss;
  const rows = f.sent.filter((r) => inWin(r.date, w));
  const value = rows.reduce((a, r) => a + (num(r.value) || 0), 0);
  return { value: rows.length, rows: rows.map((r) => ({ title: bidTitle(r), date: r.date })), basis: `${plural(rows.length, 'bid')} sent, ${money(value)} before tax`, notes: f.leftOut.changeOrders ? ['change orders aren’t counted'] : [] };
}

function wonRows(f, w) { return f.won.filter((r) => inWin(r.date, w)); }
export function salesWon(dept, w, data = {}) {
  const { f, miss } = feedFor('sales', w, data); if (miss) return miss;
  const rows = wonRows(f, w);
  return { value: rows.length, rows: rows.map((r) => ({ title: bidTitle(r), date: r.date })), basis: `${plural(rows.length, 'bid')} signed`, notes: [] };
}
export function salesWonValue(dept, w, data = {}) {
  const { f, miss } = feedFor('sales', w, data); if (miss) return miss;
  const rows = wonRows(f, w);
  const value = Math.round(rows.reduce((a, r) => a + (num(r.value) || 0), 0));
  return { value, rows: rows.map((r) => ({ title: bidTitle(r), date: r.date })), basis: `${plural(rows.length, 'bid')} signed, ${money(value)} before tax`, notes: ['change orders aren’t counted'] };
}
export function salesWinRate(dept, w, data = {}) {
  const { f, miss } = feedFor('sales', w, data); if (miss) return miss;
  const won = wonRows(f, w); const lost = f.lost.filter((r) => inWin(r.date, w));
  const n = won.length + lost.length;
  if (!n) return { value: null, rows: [], basis: 'no bids decided in this period', notes: [] };
  return {
    value: Math.round((won.length / n) * 100),
    rows: [...won.map((r) => ({ title: `WON · ${bidTitle(r)}`, date: r.date })), ...lost.map((r) => ({ title: `LOST · ${bidTitle(r)}`, date: r.date }))].sort((a, b) => b.date.localeCompare(a.date)),
    basis: `${won.length} won of ${plural(n, 'bid')} decided (${lost.length} declined / expired)`,
    notes: ['bids still out aren’t in the rate'],
  };
}

export function financeCollected(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const rows = f.collected.filter((r) => inWin(r.date, w));
  const refunds = rows.filter((r) => (num(r.amount) || 0) < 0);
  const value = Math.round(rows.reduce((a, r) => a + (num(r.amount) || 0), 0) * 100) / 100;
  return {
    value, rows: rows.map((r) => ({ title: [r.number, r.customer, money2(r.amount)].filter(Boolean).join(' · '), date: r.date })),
    basis: `${plural(rows.length - refunds.length, 'payment')} recorded`, notes: refunds.length ? [`${plural(refunds.length, 'refund')} taken off`] : [],
  };
}
export function financeInvoicesSent(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const rows = f.sent.filter((r) => inWin(r.date, w));
  return { value: rows.length, rows: rows.map((r) => ({ title: [r.number, r.customer, money2(r.total)].filter(Boolean).join(' · '), date: r.date })), basis: `${plural(rows.length, 'invoice')} sent`, notes: f.leftOut.draftInvoices ? [`${plural(f.leftOut.draftInvoices, 'draft')} not sent yet — not counted`] : [] };
}
export function financePaidWithin14(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const paid = f.paid.filter((r) => inWin(r.date, w));
  const timed = paid.filter((r) => num(r.days) != null);
  const notes = [];
  if (paid.length > timed.length) notes.push(`${plural(paid.length - timed.length, 'invoice')} with no sent date left out`);
  if (f.leftOut.paidNoPayment) notes.push(`${plural(f.leftOut.paidNoPayment, 'invoice')} marked paid with no payment recorded — left out`);
  if (!timed.length) return { value: null, rows: [], basis: 'no invoices paid in this period', notes };
  const fast = timed.filter((r) => r.days <= 14).length;
  return {
    value: Math.round((fast / timed.length) * 100),
    rows: timed.map((r) => ({ title: `${[r.number, r.customer].filter(Boolean).join(' · ')} · ${r.days} d${r.days > 14 ? ' — late' : ''}`, date: r.date })),
    basis: `${fast} of ${plural(timed.length, 'invoice')} paid within 14 days of being sent`, notes,
  };
}

/** A queue (bank lines / receipts) as it stands: the ones more than 7 days old, counted on the device from the rows. */
function staleQueue(q, data, title) {
  const today = localDateStr(data.now || Date.now());
  const cut = addDaysStr(today, -STALE_DAYS);
  const complete = q.rows.length >= q.count;
  const stale = q.rows.filter((r) => r.date < cut);
  const value = complete ? stale.length : q.over7;   // the doc lists 50 oldest: past that, the books' count
  return { value, rows: stale.slice(0, 50).map((r) => ({ title: title(r), date: r.date })), newer: Math.max(0, q.count - value) };
}
export function financeBankStale(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const x = staleQueue(f.bank, data, (r) => [r.description || 'Bank line', money2(r.amount)].join(' · '));
  return { value: x.value, rows: x.rows, basis: `${plural(x.value, 'bank line')} not reconciled, more than ${STALE_DAYS} days old`, notes: x.newer ? [`${plural(x.newer, 'newer line')} waiting — not counted yet`] : [] };
}
export function financeReceiptsStale(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const x = staleQueue(f.receipts, data, (r) => [r.vendor || 'Receipt', r.total == null ? null : money2(r.total), r.status].filter(Boolean).join(' · '));
  return { value: x.value, rows: x.rows, basis: `${plural(x.value, 'receipt')} not posted, more than ${STALE_DAYS} days old`, notes: x.newer ? [`${plural(x.newer, 'newer receipt')} waiting — not counted yet`] : [] };
}
export function financeFilingsOnTime(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const today = localDateStr(data.now || Date.now());
  const due = f.filings.filter((r) => r.due >= w.from && r.due <= (w.end || w.to));   // the whole period; later ones aren't judged yet
  const judged = due.map((r) => ({ ...r, onTime: r.status === 'done' ? true : r.due < today ? false : null })).filter((r) => r.onTime != null);
  const upcoming = due.length - judged.length;
  const notes = ['done counts as on time — when each was filed isn’t kept for every filing'];
  if (upcoming) notes.push(`${plural(upcoming, 'filing')} due later in the period — not counted yet`);
  if (!judged.length) return { value: 100, rows: [], basis: 'nothing came due in this period', notes };
  const ok = judged.filter((r) => r.onTime).length;
  return { value: Math.round((ok / judged.length) * 100), rows: judged.map((r) => ({ title: `${r.label} · ${r.onTime ? 'on time' : 'LATE'}`, date: r.due })), basis: `${ok} of ${plural(judged.length, 'filing')} due done on time`, notes };
}
export function financeBudgetsOnTrack(dept, w, data = {}) {
  const { f, miss } = feedFor('finance', w, data); if (miss) return miss;
  const rows = f.budgets.rows;
  if (!rows.length) return { value: null, missing: 'budgets', rows: [], basis: 'no money budgets set (CFO view → Budgets)', notes: [] };
  const off = rows.filter((r) => !r.onTrack);
  return { value: Math.round(((rows.length - off.length) / rows.length) * 100), rows: off.map((r) => ({ label: `${r.name} · ${money(r.actual)} of ${money(r.budget)}${num(r.forecastEnd) > num(r.budget) ? ' (heading over)' : ''}` })), basis: `${rows.length - off.length} of ${plural(rows.length, 'money budget')} on track`, notes: ['hour and metric budgets are counted in the CFO view, not here'] };
}

export const BOOKS_COUNTS = Object.freeze({
  'sales:leads': salesLeads, 'sales:estimatesSent': salesSent, 'sales:won': salesWon, 'sales:wonValue': salesWonValue, 'sales:winRate': salesWinRate,
  'finance:collected': financeCollected, 'finance:invoicesSent': financeInvoicesSent, 'finance:paidWithin14Pct': financePaidWithin14,
  'finance:bankRecStale': financeBankStale, 'finance:receiptsStale': financeReceiptsStale, 'finance:filingsOnTimePct': financeFilingsOnTime,
  'finance:budgetsOnTrackPct': financeBudgetsOnTrack,
});
